import React, { useMemo, useState } from 'react';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { asDate } from '../lib/formatters';
import { getActiveStoreNames } from '../lib/ontimeSettings';
import { isDemoOrGhostOrder } from '../lib/liveOrderFilters';

const FALLBACK_STORES = ['District Six', 'Parow'];
const STORE_COORDS = {
  'District Six': { x: 42, y: 58, area: 'CBD / District Six' },
  Parow: { x: 62, y: 36, area: 'Northern Suburbs' },
  Bothasig: { x: 56, y: 28, area: 'North' },
  'Kuils River': { x: 76, y: 48, area: 'East' },
  Crawford: { x: 48, y: 64, area: 'Southern Suburbs' },
};

const STORE_GEO = {
  'District Six': { lat: -33.9289, lng: 18.4241, area: 'CBD / District Six' },
  Parow: { lat: -33.9057, lng: 18.5992, area: 'Northern Suburbs' },
  Bothasig: { lat: -33.8603, lng: 18.5431, area: 'North' },
  'Kuils River': { lat: -33.9346, lng: 18.6817, area: 'East' },
  Crawford: { lat: -33.9786, lng: 18.5066, area: 'Southern Suburbs' },
};

const DEFAULT_MAP_BOUNDS = {
  north: -33.78,
  south: -34.08,
  west: 18.30,
  east: 18.72,
};

const NETWORK_MAP_ZOOM = 11;
const FOCUS_MAP_ZOOM = 14;

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function firstText(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function minutesSince(value, now = Date.now()) {
  const date = asDate(value);
  if (!date) return 0;
  return Math.max(0, Math.floor((now - date.getTime()) / 60000));
}

function fmtMins(mins) {
  const safe = Math.max(0, Number(mins || 0));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function orderStore(order = {}) {
  return firstText(order.store, order.raw?.storeName, order.raw?.store, 'Unassigned site');
}

function orderNumber(order = {}) {
  return firstText(order.orderNumber, order.raw?.orderNumber, order.id, '000000');
}

function customerAddress(order = {}) {
  const raw = order.raw || {};
  const customer = raw.customer || {};
  const delivery = raw.delivery || {};
  return firstText(
    order.customerAddress,
    order.deliveryAddress,
    order.address,
    customer.address,
    delivery.address,
    raw.customerAddress,
    raw.deliveryAddress,
    raw.address
  );
}

function storeAddressFromName(store = '') {
  const fallback = {
    'District Six': '23A Buitenkant Street, District Six, Cape Town, South Africa',
    Parow: '104B Voortrekker Road, Parow, Cape Town, South Africa',
    Bothasig: '4 De Grendel Avenue, Bothasig, Cape Town, South Africa',
    'Kuils River': '116 Van Riebeeck Road, Kuils River, Cape Town, South Africa',
    Mowbray: '98 Main Road, Mowbray, Cape Town, South Africa',
    Crawford: 'Crawford, Cape Town, South Africa',
  };
  return fallback[store] || `${store}, Cape Town, South Africa`;
}

function mapsQueryUrl(query = 'Cape Town, South Africa', zoom = 12) {
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed`;
}

function mapsRouteUrl(origin = '', destination = '') {
  const from = encodeURIComponent(origin || 'Cape Town, South Africa');
  const to = encodeURIComponent(destination || origin || 'Cape Town, South Africa');
  return `https://maps.google.com/maps?saddr=${from}&daddr=${to}&output=embed`;
}

function customerName(order = {}) {
  return firstText(order.customerName, order.raw?.customer?.name, order.raw?.customerName, 'Customer');
}

function orderStage(order = {}) {
  return String(order.stage || order.raw?.stage || order.status || order.raw?.status || '').toLowerCase();
}

function isClosed(order = {}) {
  const stage = orderStage(order);
  return Boolean(order.archived || order.raw?.archived || order.archivedAt || order.raw?.archivedAt || ['archived', 'cancelled', 'canceled', 'delivered', 'complete', 'completed'].includes(stage));
}

function timingSource(order = {}) {
  const t = order.timings || {};
  const stage = orderStage(order);
  if (stage === 'packing') return t.packingStartedAt || t.storeAcceptedAt || t.sentToStoreAt || order.updatedAt || order.createdAt;
  if (stage === 'ready_for_driver') return t.readyForDriverAt || t.packedAt || t.collectedAt || order.updatedAt || order.createdAt;
  if (stage === 'out_for_delivery' || stage === 'on_route' || stage === 'collected') return t.outForDeliveryAt || t.driverAcceptedAt || t.collectedAt || order.updatedAt || order.createdAt;
  return t.swapApprovalStartedAt || t.unavailableMarkedAt || t.sentToStoreAt || order.createdAt || order.placedAt;
}

function overallAgeSource(order = {}) {
  const t = order.timings || order.raw?.timings || {};
  return order.createdAt || order.placedAt || t.createdAt || order.raw?.createdAt || order.raw?.placedAt || t.sentToStoreAt || order.updatedAt;
}

function orderOverallMinutes(order = {}, now = Date.now()) {
  return minutesSince(overallAgeSource(order), now);
}

function orderHasAdjustment(order = {}) {
  const raw = order.raw || {};
  const flags = order.flags || raw.flags || {};
  const additions = [...safeArray(order.additions), ...safeArray(raw.additions)];
  const unavailable = [order.unavailableItems, raw.unavailableItems, order.unavailableItemNames, raw.unavailableItemNames].some(Array.isArray);
  return Boolean(order.swapApproved || flags.hasStockIssue || additions.length || unavailable);
}

function isCritical(order = {}, now = Date.now()) {
  const stage = orderStage(order);
  const stageMins = minutesSince(timingSource(order), now);
  const totalMins = orderOverallMinutes(order, now);
  if (totalMins >= 45) return true;
  if (orderHasAdjustment(order) && !order.swapApproved && stageMins >= 5) return true;
  if (['new', 'waiting', 'awaiting_store', ''].includes(stage) && stageMins >= 10) return true;
  if (stage === 'packing' && stageMins >= 20) return true;
  if (stage === 'ready_for_driver' && stageMins >= 8) return true;
  if (['out_for_delivery', 'on_route', 'collected'].includes(stage) && stageMins >= 50) return true;
  return false;
}

function driverName(driver = {}) {
  return firstText(driver.name, driver.fullName, driver.displayName, driver.email, driver.id, 'Driver');
}

function driverSite(driver = {}) {
  return firstText(driver.store, driver.site, driver.activeStore, driver.assignedStore, driver.defaultStore, 'Unassigned');
}

function driverAssignedSites(driver = {}) {
  const out = [];
  const push = (value) => {
    if (!value) return;
    if (Array.isArray(value)) { value.forEach(push); return; }
    if (typeof value === 'object') { push(value.name || value.store || value.site || value.storeName || value.siteName || value.label || value.value); return; }
    String(value).split(',').map((item) => item.trim()).filter(Boolean).forEach((item) => out.push(item));
  };
  push(driver.assignedStores);
  push(driver.assignedSites);
  push(driver.stores);
  push(driver.sites);
  push(driver.storeAccess);
  push(driver.siteAccess);
  push(driver.store);
  push(driver.site);
  push(driver.activeStore);
  push(driver.assignedStore);
  push(driver.assignedSite);
  push(driver.defaultStore);
  return Array.from(new Set(out.filter(Boolean)));
}

function driverBelongsToSite(driver = {}, site = '') {
  const target = String(site || '').trim().toLowerCase();
  if (!target) return true;
  return driverAssignedSites(driver).some((name) => String(name || '').trim().toLowerCase() === target);
}

function driverMatchesOrder(driver = {}, order = {}) {
  const keys = [driver.id, driver.driverProfileId, driver.profileId, driver.driverDocId, driver.name, driver.displayName, driver.uid, driver.authUid]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);
  const orderKeys = [order.assignedDriver, order.assignedDriverName, order.driver, order.driverName, order.driverId, order.driverProfileId, order.raw?.assignedDriver, order.raw?.assignedDriverName, order.raw?.driver, order.raw?.driverName, order.raw?.driverId]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);
  return keys.some((key) => orderKeys.some((orderKey) => orderKey === key || orderKey.includes(key) || key.includes(orderKey)));
}

function driverHasActiveRun(driver = {}, orders = []) {
  return safeArray(orders).some((order) => ['ready_for_driver', 'out_for_delivery', 'collected', 'on_route'].includes(orderStage(order)) && driverMatchesOrder(driver, order));
}

function driverActiveOrder(driver = {}, orders = []) {
  return safeArray(orders).find((order) => ['ready_for_driver', 'out_for_delivery', 'collected', 'on_route'].includes(orderStage(order)) && driverMatchesOrder(driver, order)) || null;
}

function driverLocationQuery(driver = {}) {
  const lat = Number(driver.lat || driver.latitude || driver.location?.lat || driver.gps?.lat);
  const lng = Number(driver.lng || driver.longitude || driver.location?.lng || driver.gps?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return `${lat},${lng}`;
  const site = driverAssignedSites(driver)[0] || driverSite(driver);
  return storeAddressFromName(site);
}

function orderDistanceKm(order = {}) {
  const raw = order.raw || {};
  const routing = order.routing || raw.routing || {};
  const km = Number(routing.distanceKm || order.distanceKm || raw.distanceKm || routing.routeDistanceKm);
  return Number.isFinite(km) && km > 0 ? km : 0;
}

function etaMinutesForOrder(order = {}) {
  const direct = Number(order.etaMinutes || order.raw?.etaMinutes || order.routing?.etaMinutes || order.raw?.routing?.etaMinutes);
  if (Number.isFinite(direct) && direct > 0) return Math.round(direct);
  const km = orderDistanceKm(order);
  if (km > 0) return Math.max(5, Math.round(km * 1.7));
  return null;
}

function etaLabelForDriver(driver = {}, orders = []) {
  const active = driverActiveOrder(driver, orders);
  if (!active) return '-';
  const mins = etaMinutesForOrder(active);
  return mins ? `ETA ${mins}m` : 'ETA live';
}

function driverOnline(driver = {}) {
  const status = String(driver.status || driver.state || '').toLowerCase();
  return driver.online === true || ['online', 'available', 'busy', 'on_run', 'assigned'].includes(status);
}

function statusForStage(stage = '') {
  if (stage === 'packing') return { label: 'Packing', color: '#f59e0b' };
  if (stage === 'ready_for_driver') return { label: 'Ready', color: '#eab308' };
  if (stage === 'out_for_delivery' || stage === 'on_route') return { label: 'On route', color: '#8b5cf6' };
  if (stage === 'collected') return { label: 'Collected', color: '#22d3ee' };
  if (stage === 'delivered') return { label: 'Delivered', color: '#22c55e' };
  return { label: 'Waiting', color: '#3b82f6' };
}

function siteHealth(site, siteOrders, drivers, now) {
  const active = siteOrders.length;
  const critical = siteOrders.filter((order) => isCritical(order, now)).length;
  const onlineDrivers = drivers.filter((driver) => driverOnline(driver) && driverBelongsToSite(driver, site)).length;
  let score = 100;
  score -= critical * 28;
  score -= Math.max(0, active - onlineDrivers) * 18;
  if (active > 0 && onlineDrivers === 0) score -= 22;
  score = Math.max(0, Math.min(100, score));
  const tone = score < 55 ? 'risk' : score < 80 ? 'watch' : 'healthy';
  return { score, tone, active, critical, onlineDrivers };
}

function makeRoutes(activeOrders, stores, drivers) {
  return activeOrders.slice(0, 12).map((order, index) => {
    const store = orderStore(order);
    const base = stores.find((row) => row.store === store) || stores[0];
    const stage = orderStage(order);
    const status = statusForStage(stage);
    const drift = ((index % 5) - 2) * 6;
    const end = { x: Math.max(10, Math.min(88, (base?.x || 48) + 18 + drift)), y: Math.max(12, Math.min(84, (base?.y || 45) - 12 + (index % 3) * 16)) };
    const driver = drivers[index % Math.max(1, drivers.length)];
    return { order, from: base, to: end, status, driver };
  });
}

function geoToPoint(lat, lng, bounds = DEFAULT_MAP_BOUNDS) {
  const x = ((lng - bounds.west) / (bounds.east - bounds.west)) * 100;
  const y = ((bounds.north - lat) / (bounds.north - bounds.south)) * 100;
  return { x: Math.max(2, Math.min(98, x)), y: Math.max(2, Math.min(98, y)) };
}

function focusBoundsForStore(store = {}) {
  if (!Number.isFinite(Number(store.lat)) || !Number.isFinite(Number(store.lng))) return DEFAULT_MAP_BOUNDS;
  const lat = Number(store.lat);
  const lng = Number(store.lng);
  return {
    north: lat + 0.040,
    south: lat - 0.040,
    west: lng - 0.058,
    east: lng + 0.058,
  };
}

function lonToTileX(lon, zoom) {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
}

function latToTileY(lat, zoom) {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, zoom));
}

function tileToLon(x, zoom) {
  return (x / Math.pow(2, zoom)) * 360 - 180;
}

function tileToLat(y, zoom) {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, zoom);
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

function mapTiles(bounds = DEFAULT_MAP_BOUNDS, zoom = NETWORK_MAP_ZOOM) {
  const minX = lonToTileX(bounds.west, zoom);
  const maxX = lonToTileX(bounds.east, zoom);
  const minY = latToTileY(bounds.north, zoom);
  const maxY = latToTileY(bounds.south, zoom);
  const tiles = [];
  for (let x = minX; x <= maxX; x += 1) {
    for (let y = minY; y <= maxY; y += 1) {
      const left = ((tileToLon(x, zoom) - bounds.west) / (bounds.east - bounds.west)) * 100;
      const right = ((tileToLon(x + 1, zoom) - bounds.west) / (bounds.east - bounds.west)) * 100;
      const top = ((bounds.north - tileToLat(y, zoom)) / (bounds.north - bounds.south)) * 100;
      const bottom = ((bounds.north - tileToLat(y + 1, zoom)) / (bounds.north - bounds.south)) * 100;
      tiles.push({
        key: `${x}-${y}`,
        src: `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`,
        left,
        top,
        width: right - left,
        height: bottom - top,
      });
    }
  }
  return tiles;
}

function orderDestination(order = {}, index = 0, storePoint = { x: 50, y: 50 }, bounds = DEFAULT_MAP_BOUNDS) {
  const raw = order.raw || {};
  const lat = Number(order.customerLat || raw.customerLat || raw.customer?.lat || raw.deliveryLat || raw.delivery?.lat);
  const lng = Number(order.customerLng || raw.customerLng || raw.customer?.lng || raw.deliveryLng || raw.delivery?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return geoToPoint(lat, lng, bounds);
  const drift = ((index % 5) - 2) * 5;
  return { x: Math.max(7, Math.min(93, storePoint.x + 13 + drift)), y: Math.max(8, Math.min(92, storePoint.y - 11 + (index % 3) * 13)) };
}

function driverMapPoint(driver = {}, index = 0, stores = [], bounds = DEFAULT_MAP_BOUNDS) {
  const lat = Number(driver.lat || driver.latitude || driver.location?.lat || driver.gps?.lat);
  const lng = Number(driver.lng || driver.longitude || driver.location?.lng || driver.gps?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return geoToPoint(lat, lng, bounds);
  const relatedStore = stores.find((row) => row.store.toLowerCase() === driverSite(driver).toLowerCase()) || stores[index % Math.max(1, stores.length)];
  return { x: Math.max(7, Math.min(93, (relatedStore?.x || 40) + 6 + ((index % 4) - 1.5) * 4)), y: Math.max(8, Math.min(92, (relatedStore?.y || 45) - 7 + (index % 3) * 6)) };
}

export default function OnTimeControlMapPage() {
  const { orders = [], drivers = [], settings, loading } = useOnTimeLiveData();
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const now = Date.now();

  const configuredStores = useMemo(() => {
    const names = getActiveStoreNames(settings || {});
    return names.length ? names : FALLBACK_STORES;
  }, [settings]);

  const storeConfigMap = useMemo(() => {
    const map = new Map();
    safeArray(settings?.stores).forEach((store) => map.set(String(store.name || '').toLowerCase(), store));
    return map;
  }, [settings]);

  const activeOrders = useMemo(() => safeArray(orders)
    .filter((order) => configuredStores.includes(orderStore(order)))
    .filter((order) => !isClosed(order) && !isDemoOrGhostOrder(order)), [orders, configuredStores]);

  const storeRows = useMemo(() => configuredStores.map((store, index) => {
    const cfg = storeConfigMap.get(String(store).toLowerCase()) || {};
    const geo = cfg.geo || (Number.isFinite(Number(cfg.lat)) && Number.isFinite(Number(cfg.lng)) ? { lat: Number(cfg.lat), lng: Number(cfg.lng) } : null) || STORE_GEO[store];
    const projected = geo ? geoToPoint(Number(geo.lat), Number(geo.lng)) : null;
    const coords = projected ? { ...projected, area: cfg.area || STORE_GEO[store]?.area || 'Cape Town', lat: Number(geo.lat), lng: Number(geo.lng), address: cfg.address || storeAddressFromName(store) } : { ...(STORE_COORDS[store] || { x: 30 + index * 12, y: 44 + index * 7, area: 'Cape Town' }), address: cfg.address || storeAddressFromName(store) };
    const siteOrders = activeOrders.filter((order) => orderStore(order) === store);
    return { store, ...coords, ...siteHealth(store, siteOrders, safeArray(drivers), now) };
  }).sort((a, b) => {
    const rank = { risk: 0, watch: 1, healthy: 2 };
    return (rank[a.tone] ?? 2) - (rank[b.tone] ?? 2) || a.store.localeCompare(b.store);
  }), [configuredStores, storeConfigMap, activeOrders, drivers, now]);

  const selectedStore = selectedSite ? storeRows.find((row) => row.store === selectedSite) : null;
  const isSiteFocused = Boolean(selectedStore);
  const mapBounds = useMemo(() => (isSiteFocused ? focusBoundsForStore(selectedStore) : DEFAULT_MAP_BOUNDS), [isSiteFocused, selectedStore?.lat, selectedStore?.lng]);
  const mapZoom = isSiteFocused ? FOCUS_MAP_ZOOM : NETWORK_MAP_ZOOM;
  const mapStoreRows = useMemo(() => storeRows.map((row) => {
    if (Number.isFinite(Number(row.lat)) && Number.isFinite(Number(row.lng))) {
      return { ...row, ...geoToPoint(Number(row.lat), Number(row.lng), mapBounds) };
    }
    return row;
  }), [storeRows, mapBounds]);
  const selectedSiteOrders = selectedSite ? activeOrders.filter((order) => orderStore(order) === selectedSite) : [];
  const focusedOrder = selectedSiteOrders[0] || null;
  const selectedDriver = safeArray(drivers).find((driver) => String(driver.id || driver.name) === selectedDriverId);
  const selectedDriverOrder = selectedDriver ? driverActiveOrder(selectedDriver, activeOrders) : null;
  const mapEmbedUrl = useMemo(() => {
    if (selectedDriverOrder) {
      const origin = driverLocationQuery(selectedDriver);
      const destination = customerAddress(selectedDriverOrder);
      return destination ? mapsRouteUrl(origin, destination) : mapsQueryUrl(origin, 16);
    }
    if (selectedDriver) return mapsQueryUrl(driverLocationQuery(selectedDriver), 16);
    if (selectedStore) return mapsQueryUrl(selectedStore.address || storeAddressFromName(selectedStore.store), 15);
    return mapsQueryUrl('Cape Town, South Africa', 11);
  }, [selectedDriverId, selectedDriverOrder?.id, selectedDriverOrder?.updatedAt, focusedOrder?.id, focusedOrder?.updatedAt, focusedOrder?.status, selectedStore?.store]);
  const visibleMapOrders = selectedDriverOrder ? [selectedDriverOrder] : (!selectedSite ? activeOrders : []);
  const routes = makeRoutes(visibleMapOrders, mapStoreRows, safeArray(drivers));
  const onlineDrivers = safeArray(drivers).filter(driverOnline);
  const panelDrivers = onlineDrivers.filter((driver) => driverBelongsToSite(driver, selectedSite));
  const mapDrivers = selectedDriver ? [selectedDriver] : (selectedSite ? panelDrivers : onlineDrivers);
  const criticalOrders = activeOrders.filter((order) => isCritical(order, now));
  const tiles = useMemo(() => mapTiles(mapBounds, mapZoom), [mapBounds, mapZoom]);

  const waitingOrders = activeOrders.filter((order) => ['new', 'waiting', 'awaiting_store', ''].includes(orderStage(order))).length;
  const atRiskOrders = activeOrders.filter((order) => !isCritical(order, now) && minutesSince(timingSource(order), now) >= 7).length;
  const storesOnline = storeRows.filter((row) => row.active > 0 || row.onlineDrivers > 0).length;

  return <div style={pageStyle}>
    <section style={topBarStyle}>
      <div style={brandBlockStyle}>
        <div style={brandMarkStyle}>M</div>
        <div>
          <div style={brandNameStyle}>LINK</div>
          <div style={brandSubStyle}>CONTROL TOWER</div>
        </div>
      </div>
      <div style={headingBlockStyle}>
        <h1 style={compactTitleStyle}>Control Map</h1>
        <p style={compactSubtitleStyle}>Real-time overview of stores, drivers and active operations</p>
      </div>
      <div style={topMetricsStyle}>
        <MetricPill label="Active Orders" value={activeOrders.length} icon="▣" />
        <MetricPill label="Critical" value={criticalOrders.length} tone="risk" icon="▣" />
        <MetricPill label="At Risk" value={atRiskOrders} tone="watch" icon="△" />
        <MetricPill label="Drivers Online" value={onlineDrivers.length} icon="♙" />
        <MetricPill label="Stores Online" value={`${storesOnline} / ${storeRows.length || FALLBACK_STORES.length}`} icon="⌂" />
      </div>
    </section>

    <section style={controlGridStyle}>
      <aside style={panelStyle}>
        <PanelTitle title="Stores" action="" />
        <div style={filterRowStyle}>
          <div style={searchBoxStyle}>⌕ Search stores...</div>
          <button type="button" style={filterButtonStyle}>All Status⌄</button>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          {storeRows.map((row) => {
            const waiting = activeOrders.filter((order) => orderStore(order) === row.store && ['new', 'waiting', 'awaiting_store', ''].includes(orderStage(order))).length;
            const onTime = Math.max(0, row.active - row.critical - waiting);
            return <button key={row.store} type="button" onClick={() => { setSelectedSite(row.store); setSelectedDriverId(''); }} style={{ ...storeCardStyle, borderColor: healthColor(row.tone), ...(selectedSite === row.store ? selectedStoreCardStyle(row.tone) : {}) }}>
              <div style={storeCardTopStyle}>
                <span style={{ ...storeIconStyle, background: `${healthColor(row.tone)}22`, color: healthColor(row.tone) }}>⌂</span>
                <div style={{ minWidth: 0 }}>
                  <strong style={storeNameStyle}>{row.store}</strong>
                  <div style={{ ...storeStatusStyle, color: healthColor(row.tone) }}>● {row.tone === 'risk' ? 'Critical' : row.tone === 'watch' ? 'Attention' : 'Healthy'}</div>
                </div>
                <span style={chevronStyle}>›</span>
              </div>
              <div style={storeMetricGridStyle}>
                <span style={storeMetricItemStyle}><small>Waiting</small><b>{waiting}</b></span>
                <span style={storeMetricItemStyle}><small>On Time</small><b>{onTime}</b></span>
                <span style={{ ...storeMetricItemStyle, color: row.critical ? '#fb7185' : '#cbd5e1' }}><small>At Risk</small><b>{row.critical}</b></span>
              </div>
              <div style={lastUpdateStyle}>Last update: live</div>
            </button>;
          })}
        </div>
        <button type="button" onClick={() => { setSelectedSite(''); setSelectedDriverId(''); }} style={wideGhostButtonStyle}>⇄ View all stores</button>
      </aside>

      <main style={mapShellStyle}>
        <div style={mapTabsStyle}>
          <div style={segmentStyle}><button style={segmentActiveStyle}>Map</button><button style={segmentButtonStyle}>Traffic</button><button style={segmentButtonStyle}>Heatmap</button></div>
          <button type="button" style={liveViewButtonStyle}>Live View⌄</button>
          <div style={mapLegendBarStyle}>
            <Legend color="#8b5cf6" label="Stores" />
            <Legend color="#22d3ee" label="Drivers" />
            <Legend color="#38bdf8" label="Active Runs" />
            <Legend color="#64748b" label="Completed" />
            <Legend color="#f59e0b" label="At Risk" />
          </div>
        </div>
        <div style={mapCanvasStyle}>
          <iframe
            title={isSiteFocused ? `${selectedStore?.store || 'Site'} live dispatch map` : 'Cape Town live dispatch map'}
            src={mapEmbedUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={googleMapFrameStyle}
          />
          <div style={mapTintStyle} />
          <div style={gridOverlayStyle} />
          <svg style={routeSvgStyle} viewBox="0 0 100 100" preserveAspectRatio="none">
            {routes.map((route, index) => {
              const destination = orderDestination(route.order, index, route.from, mapBounds);
              const risk = isCritical(route.order, now);
              return <g key={route.order.id || index}>
                <line x1={route.from?.x || 50} y1={route.from?.y || 50} x2={destination.x} y2={destination.y} stroke={risk ? '#f59e0b' : route.status.color} strokeWidth="0.62" strokeLinecap="round" opacity="0.92" strokeDasharray={risk ? '2 1.3' : ''} />
                <circle cx={destination.x} cy={destination.y} r="1.15" fill={risk ? '#f59e0b' : route.status.color} opacity="0.95" />
              </g>;
            })}
          </svg>

          {mapStoreRows.map((row) => <button key={row.store} type="button" title={`${row.store} · ${row.active} active orders`} onClick={() => { setSelectedSite(row.store); setSelectedDriverId(''); }} style={{ ...mapStorePinStyle, left: `${row.x}%`, top: `${row.y}%`, color: healthColor(row.tone), boxShadow: `0 0 44px ${healthColor(row.tone)}55`, ...(selectedSite === row.store ? { transform: 'translate(-50%, -50%) scale(1.12)' } : {}) }}>
            <span>⌂</span>
          </button>)}

          {mapDrivers.slice(0, 10).map((driver, index) => {
            const point = driverMapPoint(driver, index, mapStoreRows, mapBounds);
            return <button key={driver.id || driverName(driver)} type="button" onClick={() => setSelectedDriverId(String(driver.id || driver.name))} style={{ ...mapDriverPinStyle, left: `${point.x}%`, top: `${point.y}%` }} title={`${driverName(driver)} · ${driverHasActiveRun(driver, activeOrders) ? 'active run' : 'online, no active run'}`}>
              🚚
            </button>;
          })}

          <button type="button" style={zoomPlusStyle}>＋</button>
          <button type="button" style={zoomMinusStyle}>−</button>
          <button type="button" style={locateStyle}>⌖</button>
          {selectedDriver ? <div style={focusBadgeStyle}>Tracking: {driverName(selectedDriver)} · {selectedDriverOrder ? 'active run route' : 'last known area'}</div> : (isSiteFocused ? <div style={focusBadgeStyle}>Focused: {selectedStore.store} · Google live delivery area</div> : null)}
          <button type="button" style={unassignedStyle}>Show Unassigned Orders <b>{Math.max(0, waitingOrders)}</b></button>
        </div>
        <div style={quickActionsStyle}>
          <ActionButton icon="📣" title="Broadcast Message" text="Send update to all drivers" />
          <ActionButton icon="♙" title="Assign Driver" text="Manually assign to order" />
          <ActionButton icon="⌂" title="Create Order" text="New manual order" />
          <ActionButton icon="↪" title="Reassign Order" text="Move order to another driver" />
          <ActionButton icon="△" title="Escalate Issue" text="Raise to next level" />
          <ActionButton icon="…" title="More Actions" text="" />
        </div>
      </main>

      <aside style={panelStyle}>
        <PanelTitle title="Drivers" action="" />
        <div style={filterRowStyle}>
          <div style={searchBoxStyle}>⌕ Search drivers...</div>
          <button type="button" style={filterButtonStyle}>All Status⌄</button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {panelDrivers.slice(0, 8).map((driver, index) => {
            const online = driverOnline(driver);
            const activeRun = driverHasActiveRun(driver, activeOrders);
            return <button key={driver.id || driverName(driver)} type="button" onClick={() => setSelectedDriverId(String(driver.id || driver.name))} style={{ ...driverCardStyle, borderColor: selectedDriverId === String(driver.id || driver.name) ? 'rgba(94,231,255,.82)' : (online ? 'rgba(16,185,129,.35)' : 'rgba(148,163,184,.14)') }}>
              <span style={driverPhotoStyle}>{driverName(driver).slice(0, 1).toUpperCase()}</span>
              <div style={{ minWidth: 0 }}>
                <strong style={driverNameStyle}>{driverName(driver)}</strong>
                <div style={{ ...driverStateStyle, color: online ? '#34d399' : '#94a3b8' }}>{online ? 'Online' : 'Offline'}</div>
                <div style={driverMetaStyle}>⌖ {activeRun ? '1 Active Run' : '0 Active Runs'}</div>
              </div>
              <div style={etaStyle}>{etaLabelForDriver(driver, activeOrders)}</div>
              <span style={chevronStyle}>›</span>
            </button>;
          })}
          {!panelDrivers.length ? <div style={emptyStyle}>{selectedSite ? `No online drivers for ${selectedSite}.` : 'No online drivers found yet.'}</div> : null}
        </div>
        <button type="button" style={wideGhostButtonStyle}>⇄ View all drivers</button>
      </aside>
    </section>
  </div>;
}

function MetricPill({ label, value, tone, icon }) {
  const toneStyle = tone === 'risk' ? { borderColor: 'rgba(248,113,113,.38)', background: 'linear-gradient(180deg, rgba(127,29,29,.32), rgba(15,23,42,.72))' } : tone === 'watch' ? { borderColor: 'rgba(245,158,11,.34)', background: 'linear-gradient(180deg, rgba(120,53,15,.28), rgba(15,23,42,.72))' } : {};
  return <div style={{ ...metricPillStyle, ...toneStyle }}><span style={metricIconStyle}>{icon}</span><strong>{value}</strong><span>{label}</span></div>;
}

function ActionButton({ icon, title, text }) {
  return <button type="button" style={actionButtonStyle}><span style={actionIconStyle}>{icon}</span><strong>{title}</strong>{text ? <small style={actionTextStyle}>{text}</small> : <small style={actionTextStyle}>Ready when needed</small>}</button>;
}

function PanelTitle({ title, action }) {
  return <div style={panelTitleRowStyle}><h3>{title}</h3><span>{action}</span></div>;
}

function HealthPill({ tone, score }) {
  return <span style={{ ...healthPillStyle, background: `${healthColor(tone)}22`, borderColor: `${healthColor(tone)}66`, color: healthColor(tone) }}>{score}/100</span>;
}

function Legend({ color, label }) {
  return <span style={legendItemStyle}><i style={{ background: color }} />{label}</span>;
}

function DrawerCard({ title, rows }) {
  return <div style={drawerCardStyle}>
    <h4>{title}</h4>
    {rows.map(([label, value]) => <div key={label} style={drawerLineStyle}><span>{label}</span><strong>{value}</strong></div>)}
  </div>;
}

function healthColor(tone) {
  if (tone === 'risk') return '#fb7185';
  if (tone === 'watch') return '#fbbf24';
  return '#34d399';
}

const pageStyle = { display: 'grid', gap: 14, paddingBottom: 28, color: '#eefbff' };
const topBarStyle = { display: 'grid', gridTemplateColumns: '150px 250px minmax(520px,1fr)', alignItems: 'center', gap: 18, padding: '2px 0 10px' };
const brandBlockStyle = { display: 'flex', alignItems: 'center', gap: 12 };
const brandMarkStyle = { width: 48, height: 48, borderRadius: 12, display: 'grid', placeItems: 'center', fontWeight: 950, fontSize: 24, background: 'linear-gradient(135deg,#0ea5e9,#1d4ed8)', boxShadow: '0 0 28px rgba(59,130,246,.35)' };
const brandNameStyle = { fontWeight: 950, fontSize: 23, letterSpacing: '.04em' };
const brandSubStyle = { color: '#38bdf8', fontSize: 10, fontWeight: 900, letterSpacing: '.12em' };
const headingBlockStyle = { display: 'grid', gap: 4 };
const compactTitleStyle = { margin: 0, fontSize: 20, fontWeight: 900 };
const compactSubtitleStyle = { margin: 0, color: '#9fb1ca', fontSize: 12 };
const topMetricsStyle = { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(118px,1fr))', gap: 10 };
const utilityClusterStyle = { display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end' };
const utilityButtonStyle = { border: '1px solid rgba(148,163,184,.15)', background: 'rgba(8,20,38,.7)', color: '#fff', borderRadius: 10, padding: '11px 14px', fontWeight: 800 };
const clockChipStyle = { border: '1px solid rgba(148,163,184,.15)', background: 'rgba(8,20,38,.7)', borderRadius: 10, padding: '11px 14px', fontWeight: 900 };
const iconButtonStyle = { width: 42, height: 42, border: '1px solid rgba(148,163,184,.15)', background: 'rgba(8,20,38,.7)', color: '#fff', borderRadius: 10, display: 'grid', placeItems: 'center' };
const metricPillStyle = { border: '1px solid rgba(94,231,255,.15)', background: 'linear-gradient(180deg, rgba(15,31,54,.82), rgba(5,14,27,.88))', borderRadius: 10, padding: '10px 12px', display: 'grid', gridTemplateColumns: '28px 1fr', columnGap: 8, alignItems: 'center', color: '#eaffff', minHeight: 54, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.04)' };
const metricIconStyle = { gridRow: '1 / span 2', width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 8, background: 'rgba(14,165,233,.16)', color: '#38bdf8' };
const controlGridStyle = { display: 'grid', gridTemplateColumns: '245px minmax(760px,1fr) 245px', gap: 10, minHeight: 'calc(100vh - 145px)' };
const panelStyle = { border: '1px solid rgba(148,163,184,.13)', background: 'linear-gradient(180deg, rgba(8,20,38,.86), rgba(3,10,23,.90))', borderRadius: 10, padding: 12, boxShadow: '0 20px 60px rgba(0,0,0,.24)', minHeight: 0, overflow: 'auto' };
const panelTitleRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, color: '#eaffff' };
const filterRowStyle = { display: 'grid', gridTemplateColumns: '1fr 96px', gap: 6, marginBottom: 10 };
const searchBoxStyle = { border: '1px solid rgba(148,163,184,.12)', background: 'rgba(3,10,23,.56)', color: '#9fb1ca', borderRadius: 9, padding: '11px 12px', fontSize: 12 };
const filterButtonStyle = { border: '1px solid rgba(148,163,184,.12)', background: 'rgba(3,10,23,.56)', color: '#cbd5e1', borderRadius: 9, padding: '10px 8px', fontSize: 12 };
const storeCardStyle = { width: '100%', border: '1px solid', background: 'linear-gradient(135deg, rgba(15,23,42,.88), rgba(15,31,54,.66))', borderRadius: 10, padding: 14, color: '#fff', textAlign: 'left', cursor: 'pointer', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.035)' };
const selectedStoreCardStyle = (tone) => ({ background: `linear-gradient(135deg, ${healthColor(tone)}22, rgba(15,23,42,.86))`, boxShadow: `0 0 28px ${healthColor(tone)}22, inset 0 1px 0 rgba(255,255,255,.05)` });
const storeCardTopStyle = { display: 'grid', gridTemplateColumns: '44px 1fr 12px', gap: 12, alignItems: 'center' };
const storeIconStyle = { width: 40, height: 40, borderRadius: 999, display: 'grid', placeItems: 'center', fontSize: 22 };
const storeNameStyle = { display: 'block', fontSize: 16, marginBottom: 3 };
const storeStatusStyle = { fontSize: 12, fontWeight: 800 };
const chevronStyle = { color: '#cbd5e1', fontSize: 22 };
const storeMetricGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 16, color: '#dbeafe' };
const storeMetricItemStyle = { display: 'grid', gap: 4, alignItems: 'center' };
const lastUpdateStyle = { color: '#94a3b8', fontSize: 12, marginTop: 14 };
const wideGhostButtonStyle = { width: '100%', marginTop: 12, border: '1px solid rgba(148,163,184,.14)', background: 'rgba(3,10,23,.38)', color: '#fff', borderRadius: 10, padding: 14, fontWeight: 850, textAlign: 'left' };
const mapShellStyle = { border: '1px solid rgba(148,163,184,.13)', borderRadius: 10, background: 'linear-gradient(180deg, rgba(8,20,38,.82), rgba(3,10,23,.92))', overflow: 'hidden', minHeight: 0, display: 'grid', gridTemplateRows: 'auto 1fr auto' };
const mapTabsStyle = { display: 'grid', gridTemplateColumns: '245px 110px 1fr', gap: 8, alignItems: 'center', padding: 10 };
const segmentStyle = { display: 'inline-flex', border: '1px solid rgba(148,163,184,.12)', background: 'rgba(3,10,23,.52)', borderRadius: 9, overflow: 'hidden' };
const segmentActiveStyle = { border: 0, background: 'linear-gradient(180deg, rgba(59,130,246,.48), rgba(15,23,42,.22))', color: '#fff', padding: '10px 20px', fontWeight: 900 };
const segmentButtonStyle = { border: 0, background: 'transparent', color: '#cbd5e1', padding: '10px 20px' };
const liveViewButtonStyle = { border: '1px solid rgba(148,163,184,.12)', background: 'rgba(3,10,23,.52)', color: '#fff', borderRadius: 9, padding: '10px 12px' };
const mapLegendBarStyle = { display: 'flex', gap: 18, justifyContent: 'flex-end', flexWrap: 'wrap', fontSize: 12, color: '#cbd5e1' };
const legendItemStyle = { display: 'inline-flex', gap: 6, alignItems: 'center' };
const mapCanvasStyle = { position: 'relative', height: 'calc(100vh - 220px)', minHeight: 700, margin: '0 10px', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(94,231,255,.08)', background: '#06111f' };
const googleMapFrameStyle = { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, filter: 'saturate(.98) brightness(.98) contrast(1.02)' };
const mapTintStyle = { position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(3,10,23,.03), rgba(3,10,23,.08))', pointerEvents: 'none' };
const gridOverlayStyle = { position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(94,231,255,.018) 1px, transparent 1px), linear-gradient(90deg, rgba(94,231,255,.018) 1px, transparent 1px)', backgroundSize: '72px 72px', opacity: .08, pointerEvents: 'none' };
const routeSvgStyle = { position: 'absolute', inset: 0, width: '100%', height: '100%', filter: 'drop-shadow(0 0 10px rgba(94,231,255,.35))' };
const mapStorePinStyle = { position: 'absolute', transform: 'translate(-50%, -50%)', width: 48, height: 48, borderRadius: 999, border: '1px solid currentColor', background: 'rgba(15,23,42,.82)', display: 'grid', placeItems: 'center', fontSize: 27, cursor: 'pointer' };
const mapDriverPinStyle = { position: 'absolute', transform: 'translate(-50%, -50%)', width: 34, height: 34, borderRadius: 999, border: '1px solid rgba(45,212,191,.72)', background: 'rgba(8,145,178,.58)', color: '#ccfbf1', display: 'grid', placeItems: 'center', boxShadow: '0 0 28px rgba(20,184,166,.35)', cursor: 'pointer' };
const zoomPlusStyle = { position: 'absolute', right: 12, bottom: 152, width: 42, height: 42, borderRadius: 8, border: '1px solid rgba(148,163,184,.14)', background: 'rgba(3,10,23,.76)', color: '#fff', fontSize: 24 };
const zoomMinusStyle = { position: 'absolute', right: 12, bottom: 104, width: 42, height: 42, borderRadius: 8, border: '1px solid rgba(148,163,184,.14)', background: 'rgba(3,10,23,.76)', color: '#fff', fontSize: 24 };
const locateStyle = { position: 'absolute', right: 12, bottom: 56, width: 42, height: 42, borderRadius: 8, border: '1px solid rgba(148,163,184,.14)', background: 'rgba(3,10,23,.76)', color: '#fff', fontSize: 18 };
const focusBadgeStyle = { position: 'absolute', left: 14, bottom: 20, border: '1px solid rgba(94,231,255,.18)', background: 'rgba(3,10,23,.72)', color: '#d9feff', borderRadius: 999, padding: '8px 12px', fontSize: 12, fontWeight: 900, boxShadow: '0 10px 30px rgba(0,0,0,.28)' };
const unassignedStyle = { position: 'absolute', left: '50%', bottom: 18, transform: 'translateX(-50%)', border: '1px solid rgba(96,165,250,.22)', background: 'rgba(30,58,138,.42)', color: '#cfe8ff', borderRadius: 999, padding: '10px 18px' };
const quickActionsStyle = { display: 'grid', gridTemplateColumns: 'repeat(6, minmax(112px, 1fr))', gap: 8, padding: 10 };
const actionButtonStyle = { border: '1px solid rgba(148,163,184,.12)', background: 'linear-gradient(180deg, rgba(15,31,54,.7), rgba(8,20,38,.76))', color: '#fff', borderRadius: 9, padding: '10px 12px', display: 'grid', gridTemplateColumns: '26px 1fr', gridTemplateRows: 'auto auto', gap: '3px 8px', textAlign: 'left', alignItems: 'center', minHeight: 72 };
const actionIconStyle = { width: 24, height: 24, display: 'grid', placeItems: 'center' };
const actionTextStyle = { gridColumn: '1 / -1', color: '#9fb1ca', fontSize: 11, lineHeight: 1.25, minHeight: 14 };
const driverCardStyle = { display: 'grid', gridTemplateColumns: '42px 1fr auto 12px', gap: 10, alignItems: 'center', width: '100%', border: '1px solid', background: 'rgba(15,31,54,.62)', borderRadius: 10, padding: 10, color: '#fff', textAlign: 'left', cursor: 'pointer' };
const driverPhotoStyle = { width: 42, height: 42, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, rgba(34,211,238,.35), rgba(15,23,42,.85))', color: '#cffafe', fontWeight: 950 };
const driverNameStyle = { display: 'block', fontSize: 15 };
const driverStateStyle = { fontSize: 11, fontWeight: 900, marginTop: 2 };
const driverMetaStyle = { color: '#bdefff', fontSize: 12, marginTop: 4 };
const etaStyle = { border: '1px solid rgba(148,163,184,.10)', background: 'rgba(3,10,23,.35)', borderRadius: 8, padding: '7px 9px', color: '#dbeafe', fontSize: 12, whiteSpace: 'nowrap' };
const emptyStyle = { border: '1px dashed rgba(148,163,184,.22)', borderRadius: 10, padding: 14, color: '#94a3b8' };
const healthPillStyle = { border: '1px solid', borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 950 };
const drawerCardStyle = { border: '1px solid rgba(94,231,255,.14)', borderRadius: 18, padding: 14, background: 'rgba(2,8,23,.44)', color: '#fff' };
const drawerLineStyle = { display: 'flex', justifyContent: 'space-between', gap: 10, borderTop: '1px solid rgba(148,163,184,.12)', paddingTop: 10, marginTop: 10, color: '#cbd5e1' };
const smallMutedStyle = { color: '#9fb1ca', fontSize: 12, marginTop: 4 };
const orderRowStyle = { display: 'grid', gap: 4, border: '1px solid rgba(94,231,255,.12)', borderRadius: 15, padding: 11, background: 'rgba(15,31,54,.55)', color: '#fff' };
